/**
 * CardTemplate customization page — Epic 15
 *
 * Entry point: /search/customization/card-template
 *
 * Flow:
 *   entry → select surface → edit template (preset/custom) → save preview draft → rebuild preview
 *
 * Feature gating:
 *   Requires operatorStateV1 capability (same as other customization pages).
 *   Additionally checks for cardTemplateEditor capability; when absent shows a
 *   graceful "feature not available" notice instead of blocking the route.
 *
 * Layout follows the customization/fields pattern:
 *   - SearchFeaturePageShell from components/shell (title resolved via searchPageMap)
 *   - SettingsCard wrapper for content
 *   - useLocalizedMessage for i18n
 *
 * Does NOT:
 *   - rebuild page chrome
 *   - implement a Mustache compiler
 *   - build a full canvas/WYSIWYG editor
 */

import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from '@material-ui/core';
import { Alert as MuiAlert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import { usePreviewOpener } from 'features/shell/preview/usePreviewOpener';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { enqueueErrorSnackbar } from 'actions/Notifications';
import PublishProgressDialog from 'features/home/components/PublishProgressDialog';
import {
  useOperationDialogVisibility,
} from 'features/home/components/PublishProgressDialog/useOperationDialogVisibility';
import { useBundleRebuild } from 'features/home/components/Diagnostics/useBundleRebuild';
import { notifyOperatorStateRefresh } from 'features/home/livePublishEvents';
import { ADMIN_RUNTIME_COMMANDS } from 'app/runtime/adminRuntime';
import CardTemplateEditor from './CardTemplateEditor';
import TemplateHelpPanel from './TemplateHelpPanel';
import PreviewIssueFeed from './PreviewIssueFeed';
import { useCardTemplateSettings, CARD_TEMPLATE_MODES } from './useCardTemplateSettings';
import { usePreviewFeedback } from './usePreviewFeedback';
import IntlMessages from 'util/IntlMessages';

const useStyles = makeStyles((theme) => ({
  surfaceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2, 2.5),
    border: '1px solid #ddeae5',
    borderRadius: 8,
    backgroundColor: '#fff',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  surfaceSelector: {
    minWidth: 240,
  },
  surfaceLabel: {
    margin: 0,
    color: '#1e2248',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  surfaceDescription: {
    margin: theme.spacing(0.5, 0, 0),
    color: '#66708a',
    fontSize: 13,
    lineHeight: 1.5,
  },
  validationError: {
    marginBottom: theme.spacing(2),
  },
  editorCard: {
    marginBottom: theme.spacing(2.5),
  },
  previewSection: {
    marginTop: theme.spacing(2.5),
  },
}));

const DEFAULT_VIEW = 'resultsPage';

// Friendly display names for known view keys
const VIEW_LABELS = {
  resultsPage: 'Search Results Page',
  searchBox: 'Search Box (Dropdown)',
  dropdown: 'Search Dropdown',
  widget: 'Search Widget',
};

function viewLabel(view) {
  return VIEW_LABELS[view] || view;
}

/**
 * CardTemplatePage — main Epic 15 admin template editor page.
 * Mounted at /search/customization/card-template.
 */
const CardTemplatePage = () => {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const {
    searchSettingsClient,
    capabilities,
    productProfile,
    routeShopId,
    runtime,
  } = useShellAppContext();
  const currentShop = useSelector((s) => s.app?.currentShop);
  const shopId = routeShopId || currentShop;

  const [selectedView, setSelectedView] = useState(DEFAULT_VIEW);
  const {
    canOpenPreview,
    openStorefrontPreview,
  } = usePreviewOpener();
  const previewBuild = useBundleRebuild();
  const previewDialog = useOperationDialogVisibility(previewBuild.status);
  const previewBuildSupported = runtime?.supports?.(
    ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
  ) === true;

  const {
    settings,
    availableViews,
    loading,
    error,
    hasChanges,
    isSaving,
    validationError,
    updateSettings,
    save,
    reset,
  } = useCardTemplateSettings(searchSettingsClient, shopId, selectedView);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const previewFeedback = usePreviewFeedback();

  // Cloud owns this surface. Embedded plugins must explicitly advertise the
  // editor command set; an absent flag on an older plugin is fail-closed.
  const customEditorEnabled = productProfile?.id === 'cloud'
    || capabilities?.cardTemplateEditor === true;

  const views = useMemo(
    () => (availableViews.length > 0 ? availableViews : [DEFAULT_VIEW]),
    [availableViews],
  );
  const hasMultipleViews = views.length > 1;
  const showPreviewIssues = previewFeedback.previewEnabled || previewFeedback.issues.length > 0;

  useEffect(() => {
    if (availableViews.length > 0 && !availableViews.includes(selectedView)) {
      setSelectedView(availableViews[0]);
    }
  }, [availableViews, selectedView]);

  const handleViewChange = useCallback((event) => {
    setSelectedView(event.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      await save();
      if (!runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED)) {
        throw new Error('Admin runtime cannot finalize preview settings.');
      }
      await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED,
        { shopId },
      );
      notifyOperatorStateRefresh();
      if (!previewBuildSupported) {
        setSnackbar({
          open: true,
          message: t('notifications.success.saved'),
          severity: 'success',
        });
        return;
      }
      if (typeof previewBuild.reset === 'function') {
        previewBuild.reset();
      }
      previewDialog.begin();
      await previewBuild.startPreviewBuild(shopId);
    } catch (err) {
      const message = err.message || t('operation.preview.saveFailed');
      dispatch(enqueueErrorSnackbar(message));
      setSnackbar({ open: true, message, severity: 'error' });
    }
  }, [dispatch, previewBuild, previewBuildSupported, previewDialog, runtime, save, shopId, t]);

  const handleDiscard = useCallback(() => {
    reset();
    setSnackbar({ open: true, message: t('cardTemplate.snackbar.discarded'), severity: 'info' });
  }, [reset, t]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Effective settings: if custom editor not enabled, cap mode to preset
  const effectiveSettings = !customEditorEnabled
    ? { ...settings, cardTemplateMode: CARD_TEMPLATE_MODES.PRESET }
    : settings;

  const handleChange = useCallback((patch) => {
    if (!customEditorEnabled && patch.cardTemplateMode === CARD_TEMPLATE_MODES.CUSTOM) return;
    updateSettings(patch);
  }, [customEditorEnabled, updateSettings]);

  const operationPending = isSaving
    || previewBuild.status === 'queued'
    || previewBuild.status === 'running';

  return (
    <SearchFeaturePageShell
      pageId="cardTemplate"
      description={<IntlMessages id="ui.search.customization.cardTemplate.chooseHowProductCardsAreRenderedAcrossSearchSurfaces" />}
      isLoading={loading}
      error={error}
      actions={(
        <SaveActionBtnGrp
          isFetching={operationPending}
          disabled={!hasChanges}
          buttons={[
            {
              id: 'save',
              text: t('common.save'),
              action: handleSave,
              color: 'primary',
              testId: 'card-template-save-preview',
            },
            {
              id: 'discard',
              text: t('common.discard'),
              action: handleDiscard,
              color: 'secondary',
              testId: 'card-template-discard',
            },
          ]}
        />
      )}
    >
      <span data-testid="card-location-current" hidden>{selectedView}</span>
      {/* ── Surface selector ──────────────────────────────── */}
      {hasMultipleViews && (
        <div className={classes.surfaceRow} data-testid="card-location-selector">
          <div>
            <h2 className={classes.surfaceLabel}><IntlMessages id="ui.search.customization.cardTemplate.cardLocation" /></h2>
            <p className={classes.surfaceDescription}>
              <IntlMessages id="ui.search.customization.cardTemplate.chooseWhichSearchInterfaceTheseCardSettingsApplyTo" />
            </p>
          </div>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.surfaceSelector}
          >
            <InputLabel id="surface-select-label"><IntlMessages id="ui.search.customization.cardTemplate.cardLocation" /></InputLabel>
            <Select
              labelId="surface-select-label"
              value={views.includes(selectedView) ? selectedView : views[0]}
              onChange={handleViewChange}
              label={<IntlMessages id="ui.search.customization.cardTemplate.cardLocation" />}
              inputProps={{ 'data-testid': 'surface-select' }}
            >
              {views.map((view) => (
                <MenuItem key={view} value={view}>
                  {viewLabel(view)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {/* ── Validation error ────────────────────────────── */}
      {validationError && (
        <MuiAlert
          severity="error"
          className={classes.validationError}
          data-testid="validation-error"
        >
          {validationError}
        </MuiAlert>
      )}

      {/* ── Feature unavailable banner ───────────────────── */}
      {!customEditorEnabled && (
        <MuiAlert
          severity="info"
          data-testid="custom-editor-gated-notice"
          style={{ marginBottom: 16 }}
        >
          {t('cardTemplate.feature.unavailable')}
        </MuiAlert>
      )}

      {/* ── Main layout: editor + help panel ────────────── */}
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <SettingsCard className={classes.editorCard}>
            <CardTemplateEditor
              settings={effectiveSettings}
              onChange={handleChange}
              disabled={operationPending}
            />
          </SettingsCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SettingsCard>
            <TemplateHelpPanel />
          </SettingsCard>
        </Grid>
      </Grid>

      {/* ── Preview issue feed ──────────────────────────── */}
      {showPreviewIssues && (
        <div className={classes.previewSection} data-testid="preview-section">
          <SettingsCard>
            <PreviewIssueFeed {...previewFeedback} />
          </SettingsCard>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid="card-template-snackbar"
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {previewBuildSupported && <PublishProgressDialog
        open={previewDialog.open}
        operation={previewBuild.operation || 'preview'}
        status={previewBuild.status}
        error={previewBuild.error}
        onRetry={() => {
          if (typeof previewBuild.reset === 'function') {
            previewBuild.reset();
          }
          previewDialog.begin();
          previewBuild.startPreviewBuild(shopId);
        }}
        onClose={previewDialog.close}
        onOpenPreview={openStorefrontPreview}
        canOpenPreview={canOpenPreview}
        localizeMessage={t}
      />}
    </SearchFeaturePageShell>
  );
};

export default CardTemplatePage;
